import type { TokenCredential } from "@azure/identity";
import type { ReasoningEffort } from "@azure/ai-projects";
import { renderPersonaBody } from "./persona-render.js";
import { createPromptVersion } from "./foundry-agents.js";
import { LocalCliError } from "./errors.js";
import { loadPersonaFrontmatter, personaFoundryTools } from "./persona-contract.js";
import { productVisibilityFor } from "@m8t-stack/api-contract";

/**
 * Deploy an advisor persona as a Foundry prompt agent.
 *
 * Reads model + reasoning from `targets.foundry.*` in the persona frontmatter,
 * builds the instructions via `renderPersonaBody` (substituting any
 * `fillable-fields` defaults so no `{{placeholder}}` residue is left), then
 * calls `createPromptVersion` under the given `agentName`.
 *
 * The `repoRoot` may be the exact checkout root or any subdirectory — the function
 * walks up to locate the `personas/` directory. This lets callers pass
 * `process.cwd()` safely regardless of which subdirectory they are in.
 *
 * The onboarding intake persona declares fillable-fields with graceful defaults, so a
 * caller that supplies no overrides still renders a placeholder-free body.
 */
export async function deployPromptAdvisor(args: {
  credential: TokenCredential;
  endpoint: string;
  repoRoot: string;
  persona: string;
  agentName: string;
  /** Override the model from frontmatter (optional). */
  model?: string;
  /** Override specific fillable-field values; merged over frontmatter defaults (caller wins). */
  fieldOverrides?: Record<string, string>;
  /** Human-facing progress channel; forwarded to createPromptVersion. Pass only in
   *  human output modes (machine-parsed streams must stay clean). */
  onNotice?: (msg: string) => void;
}): Promise<string> {
  const { personaPath, frontmatter: fm } = loadPersonaFrontmatter(args.repoRoot, args.persona);

  const model = args.model ?? fm.targets?.foundry?.model;
  if (!model) {
    throw new LocalCliError({
      code: "ADVISOR_NO_MODEL",
      message: `Persona '${args.persona}' has no targets.foundry.model.`,
      hint: `Set targets.foundry.model in personas/${args.persona}/persona.md.`,
    });
  }

  const VALID_EFFORTS: ReadonlySet<string> = new Set([
    "none",
    "minimal",
    "low",
    "medium",
    "high",
    "xhigh",
  ]);
  const rawEffort = fm.targets?.foundry?.reasoning?.effort;
  if (rawEffort !== undefined && !VALID_EFFORTS.has(rawEffort)) {
    throw new LocalCliError({
      code: "ADVISOR_BAD_EFFORT",
      message: `Invalid reasoning.effort '${rawEffort}' in persona '${args.persona}'. Valid values: none, minimal, low, medium, high, xhigh.`,
    });
  }
  const reasoningEffort = rawEffort as ReasoningEffort | undefined;

  // Persona-declared tools, forwarded VERBATIM — composed by `personaFoundryTools`,
  // which the contract VERIFIER also calls. One function, deliberately: the verifier
  // used to compare a deployed agent against a copy of the tool definitions kept in the
  // platform, which stopped being the source of truth the moment the persona declared
  // its own toolset. Two derivations of "what tools should this agent have" is the drift,
  // not merely a duplication.
  const tools = personaFoundryTools(args.persona, fm);

  const personaVersion = fm.version === undefined ? null : String(fm.version);

  // Build a values map from fillable-fields defaults, then let caller overrides win.
  const fillableFields = fm["fillable-fields"] ?? [];
  const valuesMap: Record<string, string> = {};
  for (const field of fillableFields) {
    if (field.default !== undefined) valuesMap[field.name] = field.default;
  }
  for (const [k, v] of Object.entries(args.fieldOverrides ?? {})) valuesMap[k] = v;

  const instructions = renderPersonaBody(personaPath, valuesMap);

  return createPromptVersion({
    credential: args.credential,
    endpoint: args.endpoint,
    name: args.agentName,
    model,
    instructions,
    reasoningEffort,
    ...(tools ? { tools } : {}),
    metadata: {
      persona: args.persona,
      personaVersion,
      fillableFieldValues: JSON.stringify(valuesMap),
      // Derived here, once, from the agent NAME — so no call site can forget it
      // and no two call sites can disagree.
      productVisibility: productVisibilityFor(args.agentName),
    },
    ...(args.onNotice ? { onNotice: args.onNotice } : {}),
  });
}
